import {
  BarChartIcon,
  CalendarIcon,
  CheckboxIcon,
  CubeIcon,
  ExitIcon,
  FileTextIcon,
  GearIcon,
  HomeIcon,
  IdCardIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { animated, easings, useSpring } from '@react-spring/web';
import { useTranslation } from 'react-i18next';
import { LuSignature } from 'react-icons/lu';
import { PiUsersThree } from 'react-icons/pi';
import { useShallow } from 'zustand/react/shallow';
import { INavItems, INavGroup } from '.';
import { RoleEnum } from '@clickersphere/core/enums/RoleEnum';
import { useWindowSize } from '../../../app/hooks/useWindowSize';
import { useStore } from '../../../app/store';
import { pagePath } from '../../../app/utils/consts/pagePath';
import { useAuth } from '../../../app/hooks/useAuth';
import { useUserMe } from '../../../app/hooks/user/useUserMe';
import { useNavigate } from 'react-router-dom';

export function useSidebarController() {
  const { tenant, signOut, tenantSignIn, loggedInUserRole } = useAuth();
  const isAdministrator = loggedInUserRole === RoleEnum.ADMINISTRATOR;
  const canManageContracts =
    loggedInUserRole === RoleEnum.ADMINISTRATOR ||
    loggedInUserRole === RoleEnum.MANAGER;
  const { isOpen, toggleSidebar } = useStore(
    useShallow((state) => ({
      isOpen: state.sidebar.isOpen,
      toggleSidebar: state.sidebar.toggleSidebar,
    })),
  );
  const { isMobile } = useWindowSize();
  const navAnimation = useSpring({
    width: isMobile ? (isOpen ? '75%' : '0%') : isOpen ? '16rem' : '4rem',
    opacity: isMobile && !isOpen ? 0 : 1,
    config: { duration: 280, easing: easings.easeInOutCubic },
  });
  const { loggedInUser } = useUserMe();
  const { t } = useTranslation();
  const AnimatedNav = animated('nav');
  const navigate = useNavigate();

  const navigateTo = (path: string = 'back') => {
    if (path === 'back') {
      navigate(-1);
    } else {
      navigate(path);
    }

    if (isMobile && isOpen) {
      toggleSidebar(false);
    }
  };

  const operationItems: INavItems[] = [
    {
      description: t('sidenavbar.body.home'),
      icon: <HomeIcon className="size-5" />,
      pathToNavigate: pagePath.home,
    },
    {
      description: t('sidenavbar.body.leads'),
      icon: <PiUsersThree className="size-5" />,
      pathToNavigate: pagePath.lead.list,
    },
    {
      description: t('sidenavbar.body.events'),
      icon: <CalendarIcon className="size-5" />,
      pathToNavigate: pagePath.event.list,
    },
    {
      description: t('sidenavbar.body.todos'),
      icon: <CheckboxIcon className="size-5" />,
      pathToNavigate: pagePath.todo.list,
    },
    {
      description: t('sidenavbar.body.catalog'),
      icon: <CubeIcon className="size-5" />,
      pathToNavigate: pagePath.catalog.list,
    },
    ...(canManageContracts
      ? [
          {
            description: t('sidenavbar.body.contracts'),
            icon: <LuSignature className="size-5" />,
            pathToNavigate: pagePath.contractTemplate.list,
          },
        ]
      : []),
  ];

  const analyticsItems: INavItems[] = [
    {
      description: t('sidenavbar.body.statistics'),
      icon: <BarChartIcon className="size-5" />,
      pathToNavigate: pagePath.statistics,
    },
    {
      description: t('sidenavbar.body.reports'),
      icon: <FileTextIcon className="size-5" />,
      pathToNavigate: pagePath.reports,
    },
  ];

  const managementItems: INavItems[] = [
    {
      description: t('sidenavbar.body.team'),
      icon: <PersonIcon className="size-5" />,
      pathToNavigate: pagePath.team,
    },
    ...(isAdministrator
      ? [
          {
            description: t('sidenavbar.body.billing'),
            icon: <IdCardIcon className="size-5" />,
            pathToNavigate: pagePath.billing,
          },
        ]
      : []),
  ];

  const groups: INavGroup[] = [
    { items: operationItems },
    { labelKey: 'sidenavbar.groups.analytics', items: analyticsItems },
    { labelKey: 'sidenavbar.groups.management', items: managementItems },
  ];

  const footerItems: INavItems[] = [
    {
      description: t('sidenavbar.footer.settings'),
      icon: <GearIcon className="size-5" />,
      pathToNavigate: pagePath.settings,
      onClick: () => navigateTo(pagePath.settings),
    },
    {
      description: t('sidenavbar.footer.logout'),
      icon: <ExitIcon className="size-5 text-red-600" />,
      pathToNavigate: pagePath.login,
      onClick: signOut,
      danger: true,
    },
  ];

  return {
    isOpen,
    isMobile,
    navAnimation,
    groups,
    footerItems,
    t,
    AnimatedNav,
    user: loggedInUser,
    tenant,
    toggleSidebar,
    navigateTo,
    navigate,
    tenantSignIn,
  };
}
